import { Component, Input, ChangeDetectionStrategy, OnInit, OnDestroy, ChangeDetectorRef, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from '@angular/core';
import { CdkDragDrop, moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule, registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';

registerLocaleData(localeEs, 'es');
import { FinancialSectionService } from './services/financial-section.service';
import { FinancialSection, FinancialField, FinancialSectionTemplate } from './models/financial-section.interface';

/**
 * Componente principal del módulo financiero
 * Siguiendo principios de Clean Code - Single Responsibility
 */
@Component({
  selector: 'app-new-project-financial',
  templateUrl: './new-project-financial.component.html',
  styleUrls: ['./new-project-financial.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, DragDropModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class NewProjectFinancialComponent implements OnInit, OnDestroy {
  @Input() isViewMode: boolean = false;
  private destroy$ = new Subject<void>();
  
  sections: FinancialSection[] = [];
  templates: FinancialSectionTemplate[] = [];
  showTemplateSelector: boolean = false;
  
  // Totales calculados
  totalIncome: number = 0;
  totalExpense: number = 0;
  netProfit: number = 0;

  constructor(
    private financialService: FinancialSectionService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadSections();
    this.loadTemplates();
    this.calculateTotals();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Carga las secciones desde el servicio
   */
  private loadSections(): void {
    this.financialService.sections$
      .pipe(takeUntil(this.destroy$))
      .subscribe(sections => {
        this.sections = sections;
        this.calculateTotals();
        this.cdr.markForCheck();
      });
  }

  /**
   * Carga los templates disponibles
   */
  private loadTemplates(): void {
    this.templates = this.financialService.getTemplates();
  }

  /**
   * Calcula los totales automáticamente
   */
  private calculateTotals(): void {
    const totals = this.financialService.calculateTotals();
    this.totalIncome = totals.totalIncome;
    this.totalExpense = totals.totalExpense;
    this.netProfit = totals.netProfit;
  }

  /**
   * Maneja el drag & drop de secciones
   */
  onSectionDrop(event: CdkDragDrop<FinancialSection[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      const sections = [...this.sections];
      moveItemInArray(sections, event.previousIndex, event.currentIndex);
      this.financialService.reorderSections(sections);
    }
  }

  /**
   * Maneja el drag & drop de campos dentro de una sección
   */
  onFieldDrop(event: CdkDragDrop<FinancialField[]>, sectionId: string): void {
    if (event.previousIndex !== event.currentIndex) {
      this.financialService.reorderFields(sectionId, event.previousIndex, event.currentIndex);
    }
  }

  /**
   * Añade las secciones por defecto
   */
  addDefaultSections(): void {
    this.financialService.addSectionFromTemplate('rentabilidad-template');
    this.financialService.addSectionFromTemplate('inversion-template');
  }

  /**
   * Añade un campo vacío directamente
   */
  addEmptyField(sectionId: string): void {
    this.financialService.addFieldToSection(sectionId, {
      label: 'Nuevo campo',
      type: 'currency',
      value: null,
      required: false,
      placeholder: '€0.00'
    });
  }

  /**
   * Añade un campo vacío a una subsección específica
   */
  addEmptyFieldToSubsection(sectionId: string, subsectionId: string): void {
    this.financialService.addFieldToSubsection(sectionId, subsectionId, {
      label: 'Nuevo campo',
      type: 'currency',
      value: null,
      required: false,
      placeholder: '€0.00'
    });
  }

  /**
   * Inicia la adición de un campo (método anterior para compatibilidad)
   */
  startAddingField(sectionId: string): void {
    const label = prompt('Nombre del nuevo campo:');
    if (label && label.trim()) {
      const type = prompt('Tipo de campo (currency/percentage/number):') as 'currency' | 'percentage' | 'number' || 'currency';
      this.financialService.addFieldToSection(sectionId, {
        label: label.trim(),
        type,
        value: null,
        required: false,
        placeholder: this.getPlaceholderForType(type)
      });
    }
  }

  /**
   * Obtiene placeholder según tipo
   */
  private getPlaceholderForType(type: 'currency' | 'percentage' | 'number'): string {
    const placeholders = {
      currency: '€0.00',
      percentage: '%0.00',
      number: '0'
    };
    return placeholders[type];
  }

  /**
   * Actualiza una sección
   */
  onUpdateSection(event: { id: string; updates: Partial<FinancialSection> }): void {
    this.financialService.updateSection(event.id, event.updates);
  }

  /**
   * Elimina una sección
   */
  onDeleteSection(sectionId: string): void {
    this.financialService.deleteSection(sectionId);
  }

  /**
   * Añade un campo a una sección
   */
  onAddField(event: { sectionId: string; field: Omit<FinancialField, 'id'> }): void {
    this.financialService.addFieldToSection(event.sectionId, event.field);
  }

  /**
   * Actualiza un campo
   */
  onUpdateField(event: { sectionId: string; fieldId: string; updates: Partial<FinancialField> }): void {
    this.financialService.updateField(event.sectionId, event.fieldId, event.updates);
  }

  /**
   * Elimina un campo
   */
  onDeleteField(event: { sectionId: string; fieldId: string }): void {
    this.financialService.deleteField(event.sectionId, event.fieldId);
  }

  /**
   * Calcula el total de una subsección específica excluyendo porcentajes
   */
  getSpecificSubsectionTotal(section: FinancialSection, subId: string): number {
    if (!section || !section.subsections) return 0;
    const sub = section.subsections.find(s => s.id === subId);
    if (!sub) return 0;
    
    return sub.fields.reduce((acc, field) => {
      return acc + (field.type !== 'percentage' ? (field.value || 0) : 0);
    }, 0);
  }

  /**
   * Obtiene el subtotal o sumatorio total dependiendo del ID de subsección
   */
  getSubsectionTotal(section: FinancialSection, subsection: any): number {
    if (subsection.id === 'subtotalInversion' || (subsection.title || '').toLowerCase().includes('subtotal')) {
      return this.getSpecificSubsectionTotal(section, 'acquisitionCosts') + 
             this.getSpecificSubsectionTotal(section, 'constructionCosts');
    }
    
    if (subsection.id === 'costesTotales' || (subsection.title || '').toLowerCase().includes('costes totales')) {
       return this.getSpecificSubsectionTotal(section, 'acquisitionCosts') + 
              this.getSpecificSubsectionTotal(section, 'constructionCosts') +
              this.getSpecificSubsectionTotal(section, 'gestionesDomoblock');
    }
    
    return this.getSpecificSubsectionTotal(section, subsection.id);
  }

  /**
   * Alterna el selector de templates
   */
  toggleTemplateSelector(): void {
    this.showTemplateSelector = !this.showTemplateSelector;
  }

  /**
   * Formatea valor para display
   */
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  }

  /**
   * Obtiene clase CSS para el beneficio neto
   */
  getNetProfitClass(): string {
    return this.netProfit >= 0 ? 'profit-positive' : 'profit-negative';
  }

  /**
   * TrackBy function para optimizar rendering
   */
  trackBySectionId(index: number, section: FinancialSection): string {
    return section.id;
  }

  /**
   * TrackBy function para templates
   */
  trackByTemplateId(index: number, template: FinancialSectionTemplate): string {
    return template.id;
  }

  /**
   * TrackBy function para subsecciones
   */
  trackBySubsectionId(index: number, subsection: any): string {
    return subsection.id;
  }

  /**
   * Elimina una subsección
   */
  onDeleteSubsection(sectionId: string, subsectionId: string): void {
    // Implementar lógica para eliminar subsección
    console.log('Eliminar subsección:', sectionId, subsectionId);
  }

  /**
   * TrackBy function para campos
   */
  trackByFieldId(index: number, field: any): string {
    return field.id;
  }

  /**
   * Maneja el cambio de valor de un campo
   */
  onFieldValueChange(sectionId: string, fieldId: string, event: Event, columnType: 'percentage' | 'value'): void {
    const target = event.target as HTMLInputElement;
    const value = target?.value || '';
    const numericValue = value ? parseFloat(value) : null;
    
    // Actualizar el valor del campo
    this.financialService.updateField(sectionId, fieldId, { value: numericValue });
  }

  /**
   * Maneja el cambio de etiqueta de un campo
   */
  onFieldLabelChange(sectionId: string, fieldId: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    const newLabel = target?.value?.trim() || 'Nuevo campo';
    
    // Actualizar la etiqueta del campo
    this.financialService.updateField(sectionId, fieldId, { label: newLabel });
  }

  /**
   * Maneja el cambio del bloque de porcentaje de la etiqueta
   */
  onFieldLabelPercentageChange(sectionId: string, fieldId: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target?.value || '';
    const numericValue = value ? parseFloat(value) : undefined;
    
    // Actualizar el valor de percentage de la etiqueta del campo
    this.financialService.updateField(sectionId, fieldId, { labelPercentage: numericValue });
  }

  /**
   * Maneja la tecla Enter en los campos editables
   */
  onEnterKey(event: Event): void {
    const target = event.target as HTMLInputElement;
    target.blur();
  }
}